import {
	ApolloClient,
	ApolloLink,
	createHttpLink,
	ApolloProvider as InternalApolloProvider,
	split,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { Observable } from "@apollo/client/utilities";
import { captureException, startInactiveSpan, withScope } from "@sentry/nextjs";
import { FC, ReactNode, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useAuthState } from "../modules/auth";
import { getFreshAccessToken } from "../modules/auth/authSlice";
import type { AppDispatch } from "../redux";
import { SentryHelpers, SpanStatus } from "../sentry/sentry.helpers";
import { createAppCache } from "./cache";

interface Props {
	children?: ReactNode;
}

type SentryOperationContext = {
	transaction: ReturnType<typeof startInactiveSpan>;
	span: ReturnType<typeof startInactiveSpan>;
	// A nested object, not a top-level boolean: Apollo's setContext/getContext
	// merges the context, so only object references nested *inside* it (like
	// this one, or `transaction`/`span` themselves) are guaranteed to still be
	// the same instance when errorLink reads it back - a top-level primitive
	// written by errorLink wouldn't be visible to sentryLink's closure.
	status: { hadError: boolean };
};

function createOmitTypenameLink() {
	return new ApolloLink((operation, forward) => {
		if (operation.variables) {
			operation.variables = JSON.parse(
				JSON.stringify(operation.variables),
				omitTypename,
			);
		}

		return forward(operation);
	});
}

function omitTypename<T>(key: string, value: T) {
	return key === "__typename" ? undefined : value;
}

const ApolloProvider: FC<Props> = ({ children }) => {
	const authState = useAuthState();
	const dispatch = useDispatch<AppDispatch>();

	const apolloClient = useMemo(() => {
		const httpLink = createHttpLink({
			uri: process.env.NEXT_PUBLIC_API_GRAPHQL_URL,
		});

		const bffHttpLink = createHttpLink({
			uri: process.env.NEXT_PUBLIC_BFF_GRAPHQL_URL,
		});

		const omitTypenameInInputsLink = createOmitTypenameLink();

		// Waits for a refresh when the token has expired (e.g. after the laptop
		// slept), instead of sending this request with the old token.
		const authLink = setContext(async (_, { headers }) => {
			const accessToken = await dispatch(getFreshAccessToken());
			return {
				headers: {
					...headers,
					Authorization: accessToken ? `Bearer ${accessToken}` : "",
				},
			};
		});

		const sentryLink = new ApolloLink((operation, forward) => {
			const transaction = SentryHelpers.startTransaction({
				name: operation.operationName,
			});
			const span = startInactiveSpan({
				name: `${operation.operationName} query`,
				parentSpan: transaction,
				attributes: {
					query: operation.query.loc?.source.body ?? "",
				},
			});
			const status = { hadError: false };
			const sentryContext: SentryOperationContext = { transaction, span, status };
			operation.setContext(sentryContext);

			// A networkError errors the observable without a `next`, so spans must also end in `error`.
			return new Observable((observer) => {
				const subscription = forward(operation).subscribe({
					next: (data) => {
						if (!status.hadError) {
							transaction.setStatus(SpanStatus.Ok);
							span.setStatus(SpanStatus.Ok);
						}
						span.end();
						transaction.end();
						observer.next(data);
					},
					error: (error) => {
						span.setStatus(SpanStatus.UnknownError);
						transaction.setStatus(SpanStatus.UnknownError);
						span.end();
						transaction.end();
						observer.error(error);
					},
					complete: () => {
						observer.complete();
					},
				});
				return () => subscription.unsubscribe();
			});
		});

		const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
			if (graphQLErrors) {
				const mappedGQLErrors = graphQLErrors.map(
					({ message, locations, path }) => ({ message, locations, path }),
				);
				const message = mappedGQLErrors[0]?.message ?? "GraphQL error";
				withScope((scope) => {
					scope.setContext("graphQLErrors", { errors: mappedGQLErrors });
					// captureException always runs from this same call site, so without
					// an explicit fingerprint Sentry's stack-trace-based grouping merges
					// every distinct backend error (different operation, different
					// message) into one issue - defeating the point of capturing the
					// real message in the first place. Fingerprinting on the operation
					// and message splits genuinely different errors into separate
					// issues while still grouping repeats of the same one together.
					scope.setFingerprint(["graphql-error", operation.operationName, message]);
					captureException(new Error(message));
				});
			}

			if (networkError) {
				withScope((scope) => {
					scope.setFingerprint([
						"graphql-network-error",
						operation.operationName,
						networkError.message,
					]);
					captureException(networkError);
				});
			}

			const sentryContext = operation.getContext() as SentryOperationContext;
			sentryContext.status.hadError = true;
			sentryContext.span.setStatus(SpanStatus.UnknownError);
			sentryContext.transaction.setStatus(SpanStatus.UnknownError);
		});

		const mainLink = ApolloLink.from([
			omitTypenameInInputsLink,
			sentryLink,
			authLink,
			errorLink,
			httpLink,
		]);

		const bffLink = ApolloLink.from([
			omitTypenameInInputsLink,
			sentryLink,
			authLink,
			errorLink,
			bffHttpLink,
		]);

		// Route queries to BFF when context.clientName === "bff"
		const splitLink = split(
			(operation) => operation.getContext().clientName === "bff",
			bffLink,
			mainLink,
		);

		return new ApolloClient({
			link: splitLink,
			cache: createAppCache(),
			defaultOptions: {
				watchQuery: {
					fetchPolicy: "cache-and-network",
				},
			},
		});
		// Rebuilt (with an empty cache) only when the user changes, so one
		// user's data never shows for the next. authLink reads the current token
		// per request, so a token refresh doesn't need a new client or cache.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authState?.userId]);

	return (
		<InternalApolloProvider client={apolloClient}>
			{children}
		</InternalApolloProvider>
	);
};

export { ApolloProvider };
